import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../lib/toast';
import { 
  Factory, ChefHat, Package, ArrowRight, Scale, Clock, 
  Flame, AlertTriangle, CheckCircle, Plus, X, History,
  TrendingUp, Warehouse, MapPin, Search, Info, Calculator, RefreshCw, Printer, ClipboardList
} from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { WorkOrderSheet } from '../components/production/WorkOrderSheet';

const Production = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [loading, setLoading] = useState(true);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showWorkOrder, setShowWorkOrder] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productionMultiplier, setProductionMultiplier] = useState('1'); // Cuántas veces la receta
  const [sourceArea, setSourceArea] = useState(''); // Área de donde se descuentan insumos
  const [destinationArea, setDestinationArea] = useState('');
  const [notes, setNotes] = useState('');
  const [producing, setProducing] = useState(false);
  const [productionDate, setProductionDate] = useState(new Date().toISOString().slice(0, 10)); // Fecha personalizable
  
  // Manual adjustment for output
  const [useManualAdjustment, setUseManualAdjustment] = useState(false);
  const [actualOutputQuantity, setActualOutputQuantity] = useState(''); // Cantidad real obtenida
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Recalculate costs state
  const [recalculating, setRecalculating] = useState(false);

  // Historial de producción
  const [showHistorial, setShowHistorial] = useState(false);
  const [historialOrders, setHistorialOrders] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialFilter, setHistorialFilter] = useState('semana'); // dia, semana, mes, personalizado
  const [historialDateFrom, setHistorialDateFrom] = useState('');
  const [historialDateTo, setHistorialDateTo] = useState('');

  useEffect(() => {
    fetchData();
  }, [currentBranch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build branch filter params
      const branchParams = currentBranch 
        ? `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      const t = Date.now();
      const [productsRes, areasRes, ordersRes, summaryRes] = await Promise.all([
        api.get(`/production/available-to-produce?t=${t}${branchParams}`),
        api.get(`/inventory/areas?t=${t}`),
        api.get(`/production/orders?days=30&t=${t}${branchParams}`),
        api.get(`/production/summary?days=30&t=${t}${branchParams}`)
      ]);
      setAvailableProducts(productsRes.data);
      setAreas(areasRes.data);
      setRecentOrders(ordersRes.data.slice(0, 10));
      setSummary(summaryRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos de producción');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate all costs (processed supplies, recipes, products)
  const handleRecalculateCosts = async () => {
    setRecalculating(true);
    try {
      const res = await api.post('/production/recalculate-costs');
      const data = res.data;
      
      if (data.processed?.updated > 0 || data.recipes?.updated > 0) {
        toast.success(
          `${data.message}`,
          { duration: 5000 }
        );
        // Refresh data to show updated costs
        fetchData();
      } else {
        toast.info('Todos los costos ya están actualizados');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular costos');
    } finally {
      setRecalculating(false);
    }
  };

  // Cargar historial de producción
  const fetchHistorial = async (filter) => {
    setHistorialLoading(true);
    try {
      const branchParams = currentBranch 
        ? `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      let url = `/production/orders?`;
      
      // Calcular fechas según el filtro
      const today = new Date();
      let startDate, endDate;
      
      if (filter === 'dia') {
        startDate = today.toISOString().slice(0, 10);
        endDate = startDate;
      } else if (filter === 'semana') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().slice(0, 10);
        endDate = today.toISOString().slice(0, 10);
      } else if (filter === 'mes') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString().slice(0, 10);
        endDate = today.toISOString().slice(0, 10);
      } else if (filter === 'personalizado' && historialDateFrom && historialDateTo) {
        startDate = historialDateFrom;
        endDate = historialDateTo;
      } else {
        // Default: última semana
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().slice(0, 10);
        endDate = today.toISOString().slice(0, 10);
      }
      
      url += `start_date=${startDate}&end_date=${endDate}${branchParams}`;
      
      const res = await api.get(url);
      setHistorialOrders(res.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setHistorialLoading(false);
    }
  };

  // Abrir historial
  const openHistorial = () => {
    setShowHistorial(true);
    fetchHistorial(historialFilter);
  };

  const openProductionModal = (product) => {
    setSelectedProduct(product);
    setProductionMultiplier('1');
    setDestinationArea('');
    setNotes('');
    setUseManualAdjustment(false);
    setActualOutputQuantity('');
    setShowModal(true);
  };

  const handleProduce = async (forceProduction = false) => {
    if (!selectedProduct || !productionMultiplier || parseFloat(productionMultiplier) <= 0) {
      return toast.error('Ingresa cuántas veces la receta quieres producir');
    }

    const multiplier = parseFloat(productionMultiplier);

    // Get the output quantity (adjusted or calculated)
    const outputQty = useManualAdjustment && actualOutputQuantity 
      ? parseFloat(actualOutputQuantity) 
      : getExpectedOutput();

    if (outputQty <= 0) {
      return toast.error('La cantidad producida debe ser mayor a 0');
    }

    setProducing(true);
    try {
      const payload = {
        processed_supply_id: selectedProduct.processed_id,
        production_multiplier: multiplier,
        quantity_to_produce: outputQty,
        source_area_id: sourceArea || undefined,
        destination_area_id: destinationArea || undefined,
        notes: notes || undefined,
        is_output_adjusted: useManualAdjustment && actualOutputQuantity ? true : false,
        fecha: productionDate || undefined,
        force_production: forceProduction
      };

      const res = await api.post('/production/orders', payload);
      toast.success(res.data.message || 'Producción completada');
      setShowModal(false);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al producir';
      // Check if it's an insufficient stock error
      if (errorMsg.includes('INSUFFICIENT_STOCK')) {
        const cleanMsg = errorMsg.replace('|INSUFFICIENT_STOCK', '');
        if (window.confirm(`⚠️ ADVERTENCIA\n\n${cleanMsg}\n\n¿Deseas continuar de todos modos?\n(El stock quedará en negativo)`)) {
          handleProduce(true); // Retry with force
        }
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setProducing(false);
    }
  };

  // Calculate expected output based on multiplier
  const getExpectedOutput = () => {
    if (!selectedProduct || !productionMultiplier) return 0;
    return selectedProduct.output_quantity * parseFloat(productionMultiplier);
  };

  // Get actual output (adjusted or calculated)
  const getActualOutput = () => {
    if (useManualAdjustment && actualOutputQuantity) {
      return parseFloat(actualOutputQuantity);
    }
    return getExpectedOutput();
  };

  // Calculate actual yield percentage
  const getActualYield = () => {
    const totalInput = selectedProduct?.input_quantity * parseFloat(productionMultiplier || 1);
    if (!totalInput || totalInput <= 0) return 0;
    return (getActualOutput() / totalInput) * 100;
  };

  // Get total input for current multiplier
  const getTotalInput = () => {
    if (!selectedProduct || !productionMultiplier) return 0;
    return selectedProduct.input_quantity * parseFloat(productionMultiplier);
  };

  // Función para generar PDF de orden de producción
  const generateProductionOrderPDF = async (order) => {
    // Obtener los ingredientes de la orden o del producto elaborado
    let ingredients = order.ingredients_used || [];
    
    // Si no hay ingredientes en la orden, intentar obtenerlos del producto
    if (ingredients.length === 0 && order.processed_id) {
      try {
        const res = await api.get(`/processed-supplies/${order.processed_id}`);
        if (res.data && res.data.ingredients) {
          const multiplier = order.production_multiplier || 1;
          ingredients = res.data.ingredients.map(ing => ({
            supply_name: ing.supply_name,
            quantity: ing.quantity_required * multiplier,
            unit: ing.unit,
            is_consumable: ing.is_consumable
          }));
        }
      } catch (e) {
        console.error('Error fetching ingredients:', e);
      }
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          .header { text-align: center; border-bottom: 3px solid #f97316; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; color: #1e293b; margin-bottom: 5px; }
          .header h2 { font-size: 18px; color: #f97316; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
          .info-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
          .info-box label { font-size: 10px; color: #64748b; text-transform: uppercase; display: block; margin-bottom: 4px; }
          .info-box span { font-size: 14px; font-weight: bold; color: #1e293b; }
          .ingredients { margin-top: 20px; }
          .ingredients h3 { font-size: 14px; color: #1e293b; margin-bottom: 10px; padding: 8px; background: #fef3c7; border-radius: 6px; }
          .ingredient-list { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
          .ingredient-item { display: flex; justify-content: space-between; padding: 12px 15px; border-bottom: 1px solid #e2e8f0; }
          .ingredient-item:last-child { border-bottom: none; }
          .ingredient-item:nth-child(even) { background: #f8fafc; }
          .ingredient-name { font-weight: 500; }
          .ingredient-qty { font-weight: bold; color: #f97316; font-size: 14px; }
          .consumable { color: #8b5cf6; font-size: 10px; }
          .output { margin-top: 20px; background: #dcfce7; border: 2px solid #22c55e; border-radius: 8px; padding: 15px; text-align: center; }
          .output h3 { color: #15803d; margin-bottom: 5px; }
          .output .qty { font-size: 28px; font-weight: bold; color: #15803d; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; }
          .signature { margin-top: 40px; display: flex; justify-content: space-around; }
          .signature-line { text-align: center; }
          .signature-line .line { border-top: 1px solid #1e293b; width: 150px; margin-bottom: 5px; }
          .signature-line span { font-size: 10px; color: #64748b; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🍳 ORDEN DE PRODUCCIÓN</h1>
          <h2>${order.processed_name || order.processed_supply_name || 'Elaborado'}</h2>
        </div>
        
        <div class="info-grid">
          <div class="info-box">
            <label>Fecha de Producción</label>
            <span>${order.created_at ? new Date(order.created_at).toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('es-MX')}</span>
          </div>
          <div class="info-box">
            <label>Hora</label>
            <span>${order.created_at ? new Date(order.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString('es-MX')}</span>
          </div>
          <div class="info-box">
            <label>Área de Origen</label>
            <span>${order.source_area_name || order.source_area || 'Almacén General'}</span>
          </div>
          <div class="info-box">
            <label>Área de Destino</label>
            <span>${order.destination_area_name || order.destination_area || '-'}</span>
          </div>
        </div>
        
        <div class="ingredients">
          <h3>📦 INGREDIENTES NECESARIOS</h3>
          <div class="ingredient-list">
            ${ingredients.length > 0 ? ingredients.map(ing => `
              <div class="ingredient-item">
                <span class="ingredient-name">
                  ${ing.supply_name || ing.name}
                  ${ing.is_consumable ? '<span class="consumable">(solo costeo)</span>' : ''}
                </span>
                <span class="ingredient-qty">${(ing.quantity || ing.quantity_used || 0).toFixed(2)} ${ing.unit || 'kg'}</span>
              </div>
            `).join('') : `
              <div class="ingredient-item">
                <span class="ingredient-name">${order.base_supply_name || 'Insumo base'}</span>
                <span class="ingredient-qty">${(order.input_quantity || order.quantity_used || 0).toFixed(2)} ${order.raw_unit || 'kg'}</span>
              </div>
            `}
          </div>
        </div>
        
        <div class="output">
          <h3>✅ CANTIDAD A PRODUCIR</h3>
          <div class="qty">${(order.quantity_produced || order.output_quantity || 0).toFixed(2)} ${order.unit || 'kg'}</div>
        </div>
        
        <div class="signature">
          <div class="signature-line">
            <div class="line"></div>
            <span>Preparado por</span>
          </div>
          <div class="signature-line">
            <div class="line"></div>
            <span>Verificado por</span>
          </div>
        </div>
        
        <div class="footer">
          <span>Orden #${order.order_id || 'N/A'}</span>
          <span>Generado: ${new Date().toLocaleString('es-MX')}</span>
        </div>
      </body>
      </html>
    `;

    const container = document.createElement('div');
    container.innerHTML = html;
    document.body.appendChild(container);

    try {
      await html2pdf().set({
        margin: 10,
        filename: `orden_produccion_${order.processed_name || 'elaborado'}_${new Date().toISOString().slice(0,10)}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { format: 'a4' }
      }).from(container).save();
      toast.success('Orden de producción descargada');
    } catch (e) {
      toast.error('Error al generar PDF');
    } finally {
      document.body.removeChild(container);
    }
  };

  const filteredProducts = availableProducts.filter(p =>
    p.processed_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.base_supply_name && p.base_supply_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.ingredients && p.ingredients.some(i => i.supply_name?.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const getDifficultyColor = (level) => {
    switch(level) {
      case 'facil': return 'bg-green-100 text-green-700';
      case 'medio': return 'bg-yellow-100 text-yellow-700';
      case 'dificil': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-chivo font-extrabold flex items-center gap-3">
              <Factory className="w-8 h-8 text-accent" />
              Producción
            </h1>
            <p className="text-muted-foreground">Transformar insumos en elaborados</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowWorkOrder(true)}
              className="flex items-center gap-2"
              data-testid="work-order-btn"
            >
              <ClipboardList className="w-4 h-4" />
              Orden de Trabajo
            </Button>
            <Button
              variant="outline"
              onClick={openHistorial}
              className="flex items-center gap-2"
              data-testid="historial-btn"
            >
              <History className="w-4 h-4" />
              Historial
            </Button>
            <Button
              variant="outline"
              onClick={handleRecalculateCosts}
              disabled={recalculating}
              className="flex items-center gap-2"
              data-testid="recalculate-costs-btn"
            >
              {recalculating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Calculator className="w-4 h-4" />
              )}
              {recalculating ? 'Recalculando...' : 'Recalcular Costos'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white border rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Factory className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Producciones (30d)</p>
                      <p className="text-2xl font-bold text-gray-900">{summary.total_orders}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Package className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Elaborados</p>
                      <p className="text-2xl font-bold text-green-600">{availableProducts.length}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Costo Materias</p>
                      <p className="text-2xl font-bold text-amber-600">${summary.total_raw_cost?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Listos</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {availableProducts.filter(p => p.max_producible > 0).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Products Table */}
            <div className="bg-white border rounded-xl shadow-sm overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2">
                  <ChefHat className="w-5 h-5" />
                  Productos para Elaborar
                </h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48 h-8 bg-white text-gray-900 border-0 rounded"
                      data-testid="search-production"
                    />
                  </div>
                  <span className="bg-white/20 px-3 py-1 rounded text-sm">
                    {filteredProducts.length} productos
                  </span>
                </div>
              </div>
              
              {/* Table Header */}
              <div className="bg-gray-50 border-b px-4 py-3 grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                <div className="col-span-3">Elaborado</div>
                <div className="col-span-2">Ingredientes</div>
                <div className="col-span-2 text-center">Conversión</div>
                <div className="col-span-1 text-center">Rend.</div>
                <div className="col-span-1 text-center">M. Prima</div>
                <div className="col-span-1 text-center">Stock</div>
                <div className="col-span-2 text-center">Acción</div>
              </div>
              
              {/* Table Body */}
              <div className="divide-y max-h-[calc(100vh-480px)] overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <div className="py-12 text-center">
                    <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No hay productos elaborados configurados</p>
                    <p className="text-sm text-gray-400 mt-1">Crea insumos elaborados desde el módulo de Inventario</p>
                  </div>
                ) : (
                  filteredProducts.map((product) => {
                    const canProduce = product.max_producible > 0;
                    const ingredientNames = product.ingredients?.map(i => i.supply_name).join(', ') || product.base_supply_name;
                    
                    return (
                      <div 
                        key={product.processed_id}
                        className={`px-4 py-3 grid grid-cols-12 gap-2 items-center hover:bg-gray-50 transition-colors ${!canProduce ? 'bg-red-50/50' : ''}`}
                      >
                        {/* Name */}
                        <div className="col-span-3">
                          <span className="font-bold text-purple-700">{product.processed_name}</span>
                          <p className="text-xs text-gray-500">${product.cost_per_unit?.toFixed(2)}/{product.unit}</p>
                        </div>
                        
                        {/* Ingredients */}
                        <div className="col-span-2">
                          {product.ingredient_count === 1 ? (
                            <span className="text-sm text-gray-700">{product.base_supply_name}</span>
                          ) : (
                            <div>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                {product.ingredient_count} ing.
                              </span>
                              <p className="text-xs text-gray-500 truncate mt-0.5" title={ingredientNames}>
                                {ingredientNames}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Conversion */}
                        <div className="col-span-2 text-center">
                          <div className="flex items-center justify-center gap-1 text-sm">
                            <span className="text-gray-600">{parseFloat(product.input_quantity || 0).toFixed(2)}</span>
                            <ArrowRight className="w-3 h-3 text-gray-400" />
                            <span className="font-medium">{parseFloat(product.output_quantity || 0).toFixed(2)} {product.unit}</span>
                          </div>
                        </div>
                        
                        {/* Yield */}
                        <div className="col-span-1 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                            product.yield_percentage >= 80 ? 'bg-green-100 text-green-700' :
                            product.yield_percentage >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {parseFloat(product.yield_percentage || 0).toFixed(0)}%
                          </span>
                        </div>
                        
                        {/* Raw Material */}
                        <div className="col-span-1 text-center">
                          <span className="text-sm font-medium text-blue-700">
                            {parseFloat(product.base_supply_available || 0).toFixed(2)} {product.base_supply_unit}
                          </span>
                        </div>
                        
                        {/* Stock */}
                        <div className="col-span-1 text-center">
                          <span className="text-sm font-medium text-green-700">
                            {parseFloat(product.current_stock || 0).toFixed(2)} {product.unit}
                          </span>
                        </div>
                        
                        {/* Action */}
                        <div className="col-span-2 text-center">
                          <button
                            className={`px-4 py-1.5 ${canProduce ? 'bg-purple-600 hover:bg-purple-700' : 'bg-orange-500 hover:bg-orange-600'} text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1 mx-auto`}
                            onClick={() => openProductionModal(product)}
                            data-testid={`produce-${product.processed_id}`}
                          >
                            <Plus className="w-4 h-4" />
                            {canProduce ? `Producir (máx ${parseFloat(product.max_producible || 0).toFixed(0)})` : 'Producir (sin stock)'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Recent Orders */}
            {recentOrders.length > 0 && (
              <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-4 py-3">
                  <h2 className="font-bold flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Producciones Recientes
                  </h2>
                </div>
                <div className="divide-y max-h-64 overflow-y-auto">
                  {recentOrders.map((order) => (
                    <div 
                      key={order.order_id}
                      className="px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <ChefHat className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{order.processed_supply_name}</p>
                          <p className="text-xs text-gray-500">
                            {parseFloat(order.raw_quantity_used).toFixed(2)} {order.raw_unit} de {order.base_supply_name} → {order.destination_area_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-purple-700">{parseFloat(order.quantity_produced).toFixed(2)} {order.unit}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('es-MX', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Production Modal */}
      {showWorkOrder && (
        <WorkOrderSheet products={availableProducts} onClose={() => setShowWorkOrder(false)} />
      )}

      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Factory className="w-5 h-5 text-accent" />
                  Producir {selectedProduct.processed_name}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              <div className="space-y-4">
                {/* Recipe Summary */}
                <div className="bg-slate-100 rounded-lg p-3 text-center">
                  <p className="text-sm text-muted-foreground">Receta base</p>
                  <p className="font-bold text-lg">
                    {parseFloat(selectedProduct.input_quantity || 0).toFixed(2)} kg de entrada → {parseFloat(selectedProduct.output_quantity || 0).toFixed(2)} {selectedProduct.unit}
                  </p>
                  <p className="text-sm text-accent">Rendimiento: {parseFloat(selectedProduct.yield_percentage || 0).toFixed(1)}%</p>
                </div>

                {/* Ingredients List */}
                {selectedProduct.ingredients && selectedProduct.ingredients.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Ingredientes de la receta (1x):
                    </h4>
                    <div className="space-y-1 text-sm">
                      {selectedProduct.ingredients.map((ing) => (
                        <div key={ing.supply_id} className={`flex justify-between items-center ${ing.is_consumable ? 'bg-purple-50 px-2 py-0.5 rounded -mx-2' : ''}`}>
                          <span className={`flex items-center gap-1 ${ing.is_limiting ? 'text-red-600 font-medium' : ''}`}>
                            {ing.supply_name}
                            {ing.is_limiting && <span className="text-xs">(limitante)</span>}
                            {ing.is_consumable && (
                              <span className="bg-purple-200 text-purple-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                Solo costeo
                              </span>
                            )}
                          </span>
                          <span>
                            {parseFloat(ing.quantity_required || 0).toFixed(2)} {ing.unit}
                            {!ing.is_consumable && (
                              <span className="text-muted-foreground ml-1">(hay {parseFloat(ing.available || 0).toFixed(2)})</span>
                            )}
                          </span>
                        </div>
                      ))}
                      <div className="border-t pt-1 mt-2 font-bold flex justify-between">
                        <span>Total entrada:</span>
                        <span>{parseFloat(selectedProduct.input_quantity || 0).toFixed(2)} kg</span>
                      </div>
                    </div>
                    {/* Info about consumables */}
                    {selectedProduct.ingredients.some(ing => ing.is_consumable) && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-purple-700 bg-purple-100 rounded p-2">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>Los items marcados &quot;Solo costeo&quot; se incluyen en el costo pero NO se descuentan del inventario (ej: servilletas, bolsas).</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Multiplier Input */}
                <div>
                  <Label className="flex items-center gap-2 text-base font-semibold">
                    <Factory className="w-4 h-4 text-accent" />
                    ¿Cuántas veces la receta?
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max={selectedProduct.max_batches}
                      value={productionMultiplier}
                      onChange={(e) => {
                        setProductionMultiplier(e.target.value);
                        if (!useManualAdjustment && e.target.value) {
                          const expected = selectedProduct.output_quantity * parseFloat(e.target.value);
                          setActualOutputQuantity(expected.toFixed(2));
                        }
                      }}
                      placeholder="1"
                      className="text-lg w-32"
                      data-testid="multiplier-input"
                    />
                    <span className="text-sm text-muted-foreground">
                      veces (máx: {parseFloat(selectedProduct.max_batches || 0).toFixed(1)})
                    </span>
                  </div>
                </div>

                {/* What will be consumed */}
                {productionMultiplier && parseFloat(productionMultiplier) > 0 && selectedProduct.ingredients && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <h4 className="font-medium text-amber-700 mb-2">Se consumirán del inventario:</h4>
                    <div className="space-y-1 text-sm">
                      {selectedProduct.ingredients.filter(ing => !ing.is_consumable).map((ing) => {
                        const qty = ing.quantity_required * parseFloat(productionMultiplier);
                        const hasEnough = typeof ing.available === 'number' && qty <= ing.available;
                        return (
                          <div key={ing.supply_id} className={`flex justify-between ${!hasEnough ? 'text-red-600' : ''}`}>
                            <span>{ing.supply_name}</span>
                            <span>{qty.toFixed(2)} {ing.unit} {!hasEnough && '⚠️'}</span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Consumables - only for costing */}
                    {selectedProduct.ingredients.some(ing => ing.is_consumable) && (
                      <div className="mt-2 pt-2 border-t border-amber-300">
                        <h5 className="text-xs font-medium text-purple-700 mb-1">Solo para costeo (NO se descuentan):</h5>
                        <div className="space-y-0.5 text-xs text-purple-600">
                          {selectedProduct.ingredients.filter(ing => ing.is_consumable).map((ing) => {
                            const qty = ing.quantity_required * parseFloat(productionMultiplier);
                            return (
                              <div key={ing.supply_id} className="flex justify-between">
                                <span>{ing.supply_name}</span>
                                <span>{qty.toFixed(2)} {ing.unit}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Expected Output */}
                {productionMultiplier && parseFloat(productionMultiplier) > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ChefHat className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">Obtendrás:</span>
                    </div>
                    <p className="text-2xl font-bold text-green-700 text-center">
                      {getExpectedOutput().toFixed(2)} {selectedProduct.unit}
                    </p>
                    <p className="text-sm text-green-600 text-center mt-1">
                      de {selectedProduct.processed_name}
                    </p>
                  </div>
                )}

                {/* Manual Adjustment Toggle */}
                {productionMultiplier && parseFloat(productionMultiplier) > 0 && (
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium">¿La cantidad obtenida fue diferente?</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setUseManualAdjustment(!useManualAdjustment);
                          if (!useManualAdjustment) {
                            setActualOutputQuantity(getExpectedOutput().toFixed(2));
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          useManualAdjustment ? 'bg-accent' : 'bg-slate-200'
                        }`}
                        data-testid="toggle-manual-adjustment"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            useManualAdjustment ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    {useManualAdjustment && (
                      <div className="mt-3 space-y-3">
                        <p className="text-xs text-muted-foreground">
                          La receta dice que obtendrías <strong>{getExpectedOutput().toFixed(2)} {selectedProduct.unit}</strong>, 
                          pero puedes ajustar si obtuviste una cantidad diferente.
                        </p>
                        <div>
                          <Label className="text-sm">¿Cuánto obtuviste realmente?</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={actualOutputQuantity}
                              onChange={(e) => setActualOutputQuantity(e.target.value)}
                              placeholder="Cantidad real obtenida"
                              className="flex-1"
                              data-testid="actual-output-quantity"
                            />
                            <span className="text-sm text-muted-foreground whitespace-nowrap">
                              {selectedProduct.unit}
                            </span>
                          </div>
                        </div>
                        
                        {/* Yield comparison */}
                        {actualOutputQuantity && parseFloat(actualOutputQuantity) > 0 && (
                          <div className={`text-sm p-2 rounded ${
                            getActualYield() >= selectedProduct.yield_percentage 
                              ? 'bg-green-50 text-green-700' 
                              : 'bg-amber-50 text-amber-700'
                          }`}>
                            <strong>Rendimiento real:</strong> {getActualYield().toFixed(1)}%
                            {getActualYield() < selectedProduct.yield_percentage && (
                              <span className="ml-1">
                                (menor al esperado de {selectedProduct.yield_percentage}%)
                              </span>
                            )}
                            {getActualYield() > selectedProduct.yield_percentage && (
                              <span className="ml-1">
                                (mejor que el esperado de {selectedProduct.yield_percentage}%)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Source Area - De dónde se descuentan los insumos */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Warehouse className="w-4 h-4" />
                    Descontar insumos de
                  </Label>
                  <Select value={sourceArea} onValueChange={setSourceArea}>
                    <SelectTrigger className="mt-1" data-testid="source-area">
                      <SelectValue placeholder="Almacén General (por defecto)" />
                    </SelectTrigger>
                    <SelectContent>
                      {areas.map((area) => (
                        <SelectItem key={area.area_id} value={area.area_id}>
                          {area.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Área de donde se tomarán los ingredientes</p>
                </div>

                {/* Destination Area */}
                <div>
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Enviar a (opcional)
                  </Label>
                  <Select value={destinationArea} onValueChange={setDestinationArea}>
                    <SelectTrigger className="mt-1" data-testid="destination-area">
                      <SelectValue placeholder="Almacén General (por defecto)" />
                    </SelectTrigger>
                    <SelectContent>
                      {areas.map((area) => (
                        <SelectItem key={area.area_id} value={area.area_id}>
                          {area.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notas (opcional)</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Observaciones de la producción"
                  />
                </div>

                {/* Fecha de Producción */}
                <div>
                  <Label>Fecha de Producción</Label>
                  <Input
                    type="date"
                    value={productionDate}
                    onChange={(e) => setProductionDate(e.target.value)}
                    className="w-full max-w-xs"
                  />
                </div>

                {/* Cost Preview */}
                {productionMultiplier && parseFloat(productionMultiplier) > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-700">
                      <strong>Costo total de ingredientes:</strong> ${(selectedProduct.total_input_cost * parseFloat(productionMultiplier)).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
            
            {/* Fixed Action Button at Bottom */}
            <div className="p-4 border-t bg-white flex-shrink-0 rounded-b-lg">
              <Button 
                onClick={() => handleProduce(false)}
                disabled={
                  producing || 
                  !productionMultiplier || 
                  parseFloat(productionMultiplier) <= 0 ||
                  (useManualAdjustment && (!actualOutputQuantity || parseFloat(actualOutputQuantity) <= 0))
                }
                className="w-full bg-accent hover:bg-accent/90"
                data-testid="confirm-production"
              >
                {producing ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Produciendo...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Factory className="w-4 h-4" />
                    Producir {getActualOutput().toFixed(2) || 0} {selectedProduct.unit} de {selectedProduct.processed_name}
                  </span>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Historial de Producción */}
      {showHistorial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-accent" />
                  Historial de Producción
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowHistorial(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Filtros */}
              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  variant={historialFilter === 'dia' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setHistorialFilter('dia'); fetchHistorial('dia'); }}
                >
                  Hoy
                </Button>
                <Button
                  variant={historialFilter === 'semana' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setHistorialFilter('semana'); fetchHistorial('semana'); }}
                >
                  Última Semana
                </Button>
                <Button
                  variant={historialFilter === 'mes' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setHistorialFilter('mes'); fetchHistorial('mes'); }}
                >
                  Último Mes
                </Button>
                <Button
                  variant={historialFilter === 'personalizado' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setHistorialFilter('personalizado')}
                >
                  Personalizado
                </Button>
              </div>
              
              {/* Fechas personalizadas */}
              {historialFilter === 'personalizado' && (
                <div className="flex gap-2 mt-2 items-center">
                  <Input
                    type="date"
                    value={historialDateFrom}
                    onChange={(e) => setHistorialDateFrom(e.target.value)}
                    className="w-40"
                  />
                  <span className="text-muted-foreground">a</span>
                  <Input
                    type="date"
                    value={historialDateTo}
                    onChange={(e) => setHistorialDateTo(e.target.value)}
                    className="w-40"
                  />
                  <Button
                    size="sm"
                    onClick={() => fetchHistorial('personalizado')}
                    disabled={!historialDateFrom || !historialDateTo}
                  >
                    Filtrar
                  </Button>
                </div>
              )}
            </CardHeader>
            
            <CardContent className="p-0 overflow-y-auto max-h-[60vh]">
              {historialLoading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                </div>
              ) : historialOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <Package className="w-12 h-12 mb-2 opacity-50" />
                  <p>No hay producciones en este período</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Fecha</th>
                      <th className="text-left p-3 text-sm font-medium">Elaborado</th>
                      <th className="text-right p-3 text-sm font-medium">Cantidad</th>
                      <th className="text-left p-3 text-sm font-medium">Destino</th>
                      <th className="text-left p-3 text-sm font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historialOrders.map((order, idx) => (
                      <tr key={order.order_id || idx} className="border-b hover:bg-muted/30">
                        <td className="p-3 text-sm">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('es-MX', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>
                        <td className="p-3">
                          <span className="font-medium">{order.processed_name || order.processed_supply_name || '-'}</span>
                        </td>
                        <td className="p-3 text-right font-mono">
                          {order.quantity_produced?.toFixed(2) || order.output_quantity?.toFixed(2) || '0'} {order.unit || ''}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {order.destination_area_name || order.destination_area || '-'}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            order.status === 'completed' ? 'bg-green-100 text-green-700' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {order.status === 'completed' ? 'Completado' :
                             order.status === 'pending' ? 'Pendiente' :
                             order.status || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
            
            {/* Resumen */}
            {historialOrders.length > 0 && (
              <div className="border-t p-4 bg-muted/30">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total producciones: <strong>{historialOrders.length}</strong></span>
                  <span className="text-muted-foreground">
                    Total producido: <strong>
                      {historialOrders.reduce((sum, o) => sum + (o.quantity_produced || o.output_quantity || 0), 0).toFixed(2)}
                    </strong> unidades
                  </span>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default Production;
